import React, { Component, Fragment } from 'react'
import styled from 'styled-components'
import { Error, Input } from 'app/common/forms'
import { Formik } from 'formik'
import { object, string } from 'yup'
import { Loader } from 'app/common/loader'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Text, SmallText } from 'app/common/typography'
import { colors } from 'shared/assets/styles'
import {
    catalogType,
    couponOrigin,
    featureCodes,
    promoType
} from 'shared/utils'
import { SelectionScreen } from 'app/ecommerce/common/components/discounts'
import Modal from 'react-modal'
import type { ItemInfos, ProcessCoupon } from 'shared/store/ecommerce/order'
import type {
    CacPromotion,
    Coupon,
    CouponForm,
    SelectionScreenInfos
} from 'shared/types'
import type { MapById } from 'shared/store/ecommerce/catalog'
import { AlertModal } from 'app/common/modals/alert'
import { breakpoints, style } from 'app/assets/styles'
import { Recaptcha } from 'app/common/recaptcha'
import { InfoMessage } from './InfoMessage'
import { Spacer } from 'app-v2/design-systems'
import { SIZE } from 'app-v2/design-tokens'
import { FeatureToggle } from 'app/common/feature-toggle'
import { ReactElement } from 'react'

const { red } = colors
const { MENU, PRODUCT } = catalogType
const { classicModalMobileStyle, classicModalStyle } = style

type Props = {
    isOpen: boolean,
    addCouponSuccess: boolean,
    addCouponError?: string,
    handleClose: () => void,
    addCoupon: CouponForm => void,
    reset: () => void,
    promotionsById: MapById<CacPromotion>,
    collectMode: string,
    restaurantId: string,
    setCurrentItemInfos: (productInfos: ItemInfos) => void,
    processCouponNavigation: (
        coupon: Coupon,
        promotion: CacPromotion
    ) => ProcessCoupon,
    navigate: (to: string, state?: any) => void,
    intl: IntlShape,
    customTextElement?: ReactElement
}

type State = {
    selectionScreen?: SelectionScreenInfos,
    error?: string,
    showCaptcha: boolean
}

class CouponModal extends Component<Props, State> {
    recaptchaPromise: Promise<string>
    resolve: any
    reject: any

    constructor(props: Props) {
        super()
        this.state = {
            error: props.addCouponError,
            showCaptcha: false
        }
        this.initRecaptchaPromise()
    }

    componentDidUpdate(prevProps: Props): void {
        const { addCouponError } = this.props
        if (prevProps.addCouponError !== addCouponError) {
            this.setState({ error: addCouponError })
        }
    }

    initRecaptchaPromise = () => {
        this.recaptchaPromise = new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }

    verifyCallback = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            this.resolve(recaptchaToken)
        } else {
            this.reject('Error getting recaptcha token')
        }
    }

    onSubmit = async (values: any, actions: any) => {
        const { addCoupon, collectMode, restaurantId, reset } = this.props
        this.setState({ showCaptcha: true })
        try {
            reset()
            const recaptchaToken = await this.recaptchaPromise
            const coupon = await addCoupon({
                code: values.code,
                pickUpType: collectMode,
                restaurant: restaurantId,
                queen: recaptchaToken
            })
            if (coupon) {
                this.validateAndRedirect(coupon)
            }
        } finally {
            actions.setSubmitting(false)
            this.setState({ showCaptcha: false })
            this.initRecaptchaPromise()
        }
    }

    validateAndRedirect = async (coupon: Coupon) => {
        const {
            handleClose,
            intl,
            navigate,
            processCouponNavigation,
            promotionsById
        } = this.props
        const { id: couponCode, promotionId, origin } = coupon
        const isImpersonalOperation = origin === couponOrigin.IMPERSONAL_COUPON
        const promotion = promotionsById[promotionId || '']
        let errorMessage, route
        if (promotion) {
            const CCpromo = {
                couponCode,
                idReboot: promotion.idReboot,
                promoType: promotion.promoType,
                promoValue: promotion.promoValue || 0,
                promotionId,
                promotionName: promotion.name,
                threshold: promotion.threshold
            }
            const {
                listItems,
                type,
                error,
                hasOptions,
                isDeliveryFeesPromotion,
                requiredType,
                listRequiredItems
            } = await processCouponNavigation(coupon, promotion)

            if (listRequiredItems && requiredType && !error) {
                handleClose()
                this.setState({
                    selectionScreen: {
                        listItems: listRequiredItems,
                        type: requiredType,
                        isImpersonalOperation,
                        promotionToApply: CCpromo,
                        coupon
                    }
                })
                return
            }

            errorMessage = error

            if (listItems && type && !errorMessage) {
                handleClose()
                this.setState({
                    selectionScreen: {
                        listItems,
                        promotion: CCpromo,
                        type,
                        isImpersonalOperation
                    }
                })
            } else {
                switch (type) {
                    case MENU:
                        route = intl.formatMessage({
                            id: 'routes.clickandcollect.menu'
                        })
                        break
                    case PRODUCT:
                        route = hasOptions
                            ? intl.formatMessage({
                                  id: 'routes.clickandcollect.product'
                              })
                            : intl.formatMessage({
                                  id: 'routes.clickandcollect.catalog'
                              })
                        break
                    case promoType.ORDER:
                        route = intl.formatMessage({
                            id: 'routes.clickandcollect.cart.summary'
                        })
                        break
                    default:
                        if (!errorMessage) errorMessage = 'error.section.title'
                }
                if (route && !errorMessage) {
                    navigate(route, {
                        promotion: CCpromo,
                        isImpersonalOperation
                    })
                    handleClose()
                }
            }
        } else {
            errorMessage = 'error.section.title'
        }
        this.setState({ error: errorMessage })
    }

    closeSelectionScreen = () => {
        this.setState({ selectionScreen: undefined })
    }

    handleClose = () => {
        const { handleClose, reset } = this.props
        handleClose()
        reset()
    }

    render() {
        const {
            isOpen,
            handleClose,
            setCurrentItemInfos,
            customTextElement
        } = this.props
        const { error, selectionScreen, showCaptcha } = this.state

        const validationSchema = object().shape({
            code: string().required('form.error.field.mandatory')
        })

        return (
            <Formik
                initialValues={{ code: '' }}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={this.onSubmit}>
                {({
                    handleChange,
                    handleSubmit,
                    handleBlur,
                    isSubmitting,
                    errors,
                    touched
                }) => (
                    <Fragment>
                        <AlertModal
                            isOpen={isOpen}
                            handleClose={this.handleClose}
                            alertTitleKey="operation.impersonal.modal.title"
                            okLabel="form.button.valid"
                            leftLabel="form.button.cancel"
                            onOkClick={handleSubmit}
                            onLeftClick={handleClose}
                            disableTopButton={isSubmitting}>
                            <Container>
                                <Input
                                    name="code"
                                    type="text"
                                    placeholderKey="kingdom.privileges.form.placeholder"
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                />
                                {(errors.code || error) && (
                                    <ErrorsContainer>
                                        {errors.code && touched.code && (
                                            <Error errorKey={errors.code} />
                                        )}
                                        {!!error && (
                                            <SmallText color={red}>
                                                <FormattedMessage id={error} />
                                            </SmallText>
                                        )}
                                        <Loader
                                            loading={isSubmitting}
                                            style={{ margin: 5 }}
                                        />
                                    </ErrorsContainer>
                                )}
                                {showCaptcha && (
                                    <Recaptcha
                                        action="WebCCCouponForm"
                                        verifyCallback={this.verifyCallback}
                                    />
                                )}

                                {!!customTextElement && (
                                    <>
                                        <Spacer size={SIZE.XS} />
                                        {customTextElement}
                                    </>
                                )}

                                <Spacer size={SIZE.XS} />

                                <FeatureToggle
                                    features={[
                                        featureCodes.BANNIERE_WEB_BURGER_MYSTERE,
                                        featureCodes.BANNIERE_WEB_BK_DAYS,
                                        featureCodes.BANNIERE_WEB_OPERATION_REPEAT,
                                        featureCodes.BANNIERE_WEB_BAL_2_PROMO
                                    ]}>
                                    <InfoMessage />
                                </FeatureToggle>
                            </Container>
                        </AlertModal>
                        <Modal
                            isOpen={!!selectionScreen}
                            onRequestClose={this.closeSelectionScreen}
                            style={
                                window.innerWidth < breakpoints.medium
                                    ? classicModalMobileStyle
                                    : classicModalStyle
                            }>
                            {selectionScreen && (
                                <SelectionScreen
                                    selectionScreenInfos={selectionScreen}
                                    handleClose={this.closeSelectionScreen}
                                    setCurrentItemInfos={setCurrentItemInfos}
                                />
                            )}
                        </Modal>
                    </Fragment>
                )}
            </Formik>
        )
    }
}

const ErrorsContainer = styled.div`
    min-height: 15px;
`

const Container = styled.div`
    margin-top: 15px;
`

const CouponModalComponent = injectIntl(CouponModal)
export { CouponModalComponent }
